import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Users, BarChart3, FileText, Target, BookOpen, 
  Scale, TrendingUp, Calendar, Clock, Filter, Download,
  ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Loader2,
  RefreshCw, Search, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Label } from '../components/ui/label';
import SpEdKitLogo from '../components/SpEdKitLogo';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature icons mapping
const featureIcons = {
  social_stories: BookOpen,
  iep_goals: Target,
  iep_reviews: Scale,
  goal_trackers: BarChart3,
  progress_entries: TrendingUp,
  teams: Users,
  student_folders: FileText,
};

// Feature display names
const featureNames = {
  social_stories: 'Social Stories',
  iep_goals: 'IEP Goals',
  iep_reviews: 'IEP Reviews',
  goal_trackers: 'Goal Trackers',
  progress_entries: 'Progress Entries',
  teams: 'Teams',
  student_folders: 'Student Folders',
};

const Admin = ({ user }) => {
  const navigate = useNavigate();
  
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Filter states
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedFeature, setSelectedFeature] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [features, setFeatures] = useState(null);
  const [users, setUsers] = useState(null);
  const [iepSummary, setIepSummary] = useState(null);
  const [timeline, setTimeline] = useState(null);
  
  // Loading states
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingIep, setLoadingIep] = useState(false);

  useEffect(() => {
    checkAdminStatus();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchOverview();
      fetchTimeline();
    }
  }, [isAdmin, dateFrom, dateTo]);

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`, { withCredentials: true });
      setIsAdmin(response.data.is_admin);
      if (!response.data.is_admin) {
        toast.error('Admin access required');
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (selectedUser) params.append('user_id', selectedUser);
    if (selectedFeature) params.append('feature', selectedFeature);
    return params.toString();
  };

  const fetchOverview = async () => {
    setLoadingOverview(true);
    try {
      const params = buildQueryParams();
      const response = await axios.get(`${API}/admin/analytics/overview?${params}`, { withCredentials: true });
      setOverview(response.data);
    } catch (error) {
      console.error('Error fetching overview:', error);
      toast.error('Failed to load overview');
    } finally {
      setLoadingOverview(false);
    }
  };

  const fetchFeatures = async () => {
    setLoadingFeatures(true);
    try {
      const params = buildQueryParams();
      const response = await axios.get(`${API}/admin/analytics/features?${params}`, { withCredentials: true });
      setFeatures(response.data);
    } catch (error) {
      console.error('Error fetching features:', error);
      toast.error('Failed to load feature analytics');
    } finally {
      setLoadingFeatures(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const params = buildQueryParams();
      const response = await axios.get(`${API}/admin/analytics/users?${params}`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load user analytics');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchIepSummary = async () => {
    setLoadingIep(true);
    try {
      const params = buildQueryParams();
      const response = await axios.get(`${API}/admin/analytics/iep-summary?${params}`, { withCredentials: true });
      setIepSummary(response.data);
    } catch (error) {
      console.error('Error fetching IEP summary:', error);
      toast.error('Failed to load IEP summary');
    } finally {
      setLoadingIep(false);
    }
  };

  const fetchTimeline = async () => {
    try {
      const params = buildQueryParams();
      const response = await axios.get(`${API}/admin/analytics/activity-timeline?${params}`, { withCredentials: true });
      setTimeline(response.data);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'features' && !features) fetchFeatures();
    if (tab === 'users' && !users) fetchUsers();
    if (tab === 'iep' && !iepSummary) fetchIepSummary();
  };

  const applyFilters = () => {
    fetchOverview();
    fetchTimeline();
    if (features) fetchFeatures();
    if (users) fetchUsers();
    if (iepSummary) fetchIepSummary();
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedUser('');
    setSelectedFeature('');
  };

  const setQuickDateRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setDateFrom(start.toISOString().split('T')[0]);
    setDateTo(end.toISOString().split('T')[0]);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-text-primary mb-2">Access Denied</h1>
          <p className="text-text-secondary mb-6">You don&apos;t have admin privileges to access this page.</p>
          <Button onClick={() => navigate('/dashboard')} className="rounded-full">
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/dashboard')} className="text-text-muted hover:text-primary">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <SpEdKitLogo size="sm" />
              <Badge className="bg-purple-100 text-purple-800 border-purple-200">Admin</Badge>
            </div>
            <Button
              onClick={applyFilters}
              variant="outline"
              size="sm"
              className="rounded-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-text-primary">
            Admin Analytics Dashboard
          </h1>
          <p className="text-text-secondary text-sm">
            Monitor usage statistics and IEP data across the platform
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-text-muted" />
            <span className="font-medium text-sm text-text-primary">Filters</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-text-muted">Date From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-text-muted">Date To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-text-muted">Quick Range</Label>
              <div className="flex gap-2 mt-1">
                <Button size="sm" variant="outline" onClick={() => setQuickDateRange(7)}>7d</Button>
                <Button size="sm" variant="outline" onClick={() => setQuickDateRange(30)}>30d</Button>
                <Button size="sm" variant="outline" onClick={() => setQuickDateRange(90)}>90d</Button>
              </div>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={applyFilters} className="bg-primary text-white rounded-full flex-1">
                Apply
              </Button>
              <Button onClick={clearFilters} variant="outline" className="rounded-full">
                Clear
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="features" className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              <span className="hidden sm:inline">Features</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="iep" className="flex items-center gap-2">
              <Scale className="w-4 h-4" />
              <span className="hidden sm:inline">IEP Data</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {loadingOverview ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : overview ? (
              <div className="space-y-6">
                {/* Period Info */}
                <div className="text-sm text-text-muted">
                  Showing data for: {overview.period?.description || 'All time'}
                </div>

                {/* User Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-text-primary">{overview.users?.total_registered || 0}</p>
                        <p className="text-xs text-text-muted">Total Users</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-text-primary">{overview.users?.active_in_period || 0}</p>
                        <p className="text-xs text-text-muted">Active Users</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-text-primary">{overview.totals?.total_content_created || 0}</p>
                        <p className="text-xs text-text-muted">Content Created</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Scale className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-text-primary">{overview.feature_usage?.iep_reviews || 0}</p>
                        <p className="text-xs text-text-muted">IEP Reviews</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feature Usage */}
                <div className="bg-white rounded-xl border p-6">
                  <h2 className="font-heading font-bold text-lg text-text-primary mb-4">Feature Usage</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Object.entries(overview.feature_usage || {}).map(([key, value]) => {
                      const Icon = featureIcons[key] || FileText;
                      return (
                        <div key={key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <Icon className="w-5 h-5 text-primary" />
                          <div>
                            <p className="font-bold text-text-primary">{value}</p>
                            <p className="text-xs text-text-muted">{featureNames[key] || key}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Activity Timeline */}
                {timeline?.timeline && (
                  <div className="bg-white rounded-xl border p-6">
                    <h2 className="font-heading font-bold text-lg text-text-primary mb-4">Activity Timeline</h2>
                    <div className="h-48 flex items-end gap-1">
                      {timeline.timeline.slice(-30).map((day, index) => {
                        const maxTotal = Math.max(...timeline.timeline.map(d => d.total)) || 1;
                        const height = (day.total / maxTotal) * 100;
                        return (
                          <div
                            key={index}
                            className="flex-1 bg-primary/20 hover:bg-primary/40 rounded-t transition-colors relative group"
                            style={{ height: `${Math.max(height, 4)}%` }}
                          >
                            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                              {day.date}: {day.total} activities
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-xs text-text-muted mt-2">
                      <span>{timeline.timeline[0]?.date}</span>
                      <span>{timeline.timeline[timeline.timeline.length - 1]?.date}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-text-muted">
                No data available
              </div>
            )}
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features">
            {loadingFeatures ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : features?.features ? (
              <div className="space-y-6">
                {Object.entries(features.features).map(([featureKey, data]) => {
                  const Icon = featureIcons[featureKey] || FileText;
                  return (
                    <Collapsible key={featureKey}>
                      <div className="bg-white rounded-xl border overflow-hidden">
                        <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-gray-50">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                              <Icon className="w-5 h-5 text-primary" />
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-text-primary">{featureNames[featureKey] || featureKey}</p>
                              <p className="text-sm text-text-muted">
                                {data.total_count} total • {data.unique_users} users
                              </p>
                            </div>
                          </div>
                          <ChevronDown className="w-5 h-5 text-text-muted" />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="border-t p-4 space-y-4">
                            {/* Additional stats based on feature type */}
                            {data.by_domain && (
                              <div>
                                <p className="text-sm font-medium text-text-muted mb-2">By Domain</p>
                                <div className="flex flex-wrap gap-2">
                                  {Object.entries(data.by_domain).map(([domain, count]) => (
                                    <Badge key={domain} variant="outline">{domain}: {count}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {data.compliance_scores && (
                              <div>
                                <p className="text-sm font-medium text-text-muted mb-2">Compliance Scores</p>
                                <div className="flex flex-wrap gap-2">
                                  {Object.entries(data.compliance_scores).map(([score, count]) => (
                                    <Badge 
                                      key={score} 
                                      className={
                                        score === 'Strong' ? 'bg-green-100 text-green-800' :
                                        score === 'Adequate' ? 'bg-blue-100 text-blue-800' :
                                        score === 'Needs Improvement' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-red-100 text-red-800'
                                      }
                                    >
                                      {score}: {count}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {data.by_status && (
                              <div>
                                <p className="text-sm font-medium text-text-muted mb-2">By Status</p>
                                <div className="flex flex-wrap gap-2">
                                  {Object.entries(data.by_status).map(([status, count]) => (
                                    <Badge key={status} variant="outline">{status}: {count}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* Recent items */}
                            {data.recent_items?.length > 0 && (
                              <div>
                                <p className="text-sm font-medium text-text-muted mb-2">Recent Activity</p>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                  {data.recent_items.slice(0, 10).map((item, idx) => (
                                    <div key={idx} className="text-sm p-2 bg-gray-50 rounded flex justify-between">
                                      <span className="text-text-primary truncate flex-1">
                                        {item.title || item.goal_text || item.document_name || item.goal_title || item.student_name || 'Item'}
                                      </span>
                                      <span className="text-text-muted text-xs ml-2">
                                        {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Button onClick={fetchFeatures} className="rounded-full">Load Feature Analytics</Button>
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            {loadingUsers ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : users?.users ? (
              <div className="space-y-4">
                {/* Search */}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <Input
                      placeholder="Search users by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* User List */}
                <div className="bg-white rounded-xl border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left p-3 text-xs font-medium text-text-muted">User</th>
                          <th className="text-center p-3 text-xs font-medium text-text-muted">Stories</th>
                          <th className="text-center p-3 text-xs font-medium text-text-muted">Goals</th>
                          <th className="text-center p-3 text-xs font-medium text-text-muted">Reviews</th>
                          <th className="text-center p-3 text-xs font-medium text-text-muted">Trackers</th>
                          <th className="text-center p-3 text-xs font-medium text-text-muted">Total</th>
                          <th className="text-left p-3 text-xs font-medium text-text-muted">Last Active</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.users
                          .filter(u => 
                            !searchQuery || 
                            u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            u.email?.toLowerCase().includes(searchQuery.toLowerCase())
                          )
                          .map((userData, idx) => (
                            <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                              <td className="p-3">
                                <div>
                                  <p className="font-medium text-text-primary text-sm">{userData.name}</p>
                                  <p className="text-xs text-text-muted">{userData.email}</p>
                                </div>
                              </td>
                              <td className="p-3 text-center text-sm">{userData.usage?.social_stories || 0}</td>
                              <td className="p-3 text-center text-sm">{userData.usage?.iep_goals || 0}</td>
                              <td className="p-3 text-center text-sm">{userData.usage?.iep_reviews || 0}</td>
                              <td className="p-3 text-center text-sm">{userData.usage?.goal_trackers || 0}</td>
                              <td className="p-3 text-center">
                                <Badge className="bg-primary/10 text-primary">{userData.usage?.total || 0}</Badge>
                              </td>
                              <td className="p-3 text-xs text-text-muted">
                                {userData.last_activity ? new Date(userData.last_activity).toLocaleDateString() : 'Never'}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <p className="text-sm text-text-muted">
                  Showing {users.users.filter(u => !searchQuery || u.name?.toLowerCase().includes(searchQuery.toLowerCase()) || u.email?.toLowerCase().includes(searchQuery.toLowerCase())).length} of {users.total_users} users
                </p>
              </div>
            ) : (
              <div className="text-center py-12">
                <Button onClick={fetchUsers} className="rounded-full">Load User Analytics</Button>
              </div>
            )}
          </TabsContent>

          {/* IEP Data Tab */}
          <TabsContent value="iep">
            {loadingIep ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : iepSummary ? (
              <div className="space-y-6">
                {/* IEP Reviews Summary */}
                <div className="bg-white rounded-xl border p-6">
                  <h2 className="font-heading font-bold text-lg text-text-primary mb-4 flex items-center gap-2">
                    <Scale className="w-5 h-5 text-primary" />
                    IEP Reviews Summary
                  </h2>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-text-primary">{iepSummary.iep_reviews?.total_reviews || 0}</p>
                      <p className="text-xs text-text-muted">Total Reviews</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-amber-600">{iepSummary.iep_reviews?.total_concerns || 0}</p>
                      <p className="text-xs text-text-muted">Concerns Found</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-600">{iepSummary.iep_reviews?.total_strengths || 0}</p>
                      <p className="text-xs text-text-muted">Strengths Found</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-text-primary">
                        {iepSummary.iep_reviews?.total_reviews ? 
                          (iepSummary.iep_reviews.total_concerns / iepSummary.iep_reviews.total_reviews).toFixed(1) : 0}
                      </p>
                      <p className="text-xs text-text-muted">Avg Concerns/Review</p>
                    </div>
                  </div>

                  {/* Compliance Distribution */}
                  <div className="mb-6">
                    <p className="text-sm font-medium text-text-muted mb-2">Compliance Score Distribution</p>
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(iepSummary.iep_reviews?.compliance_distribution || {}).map(([score, count]) => (
                        <Badge 
                          key={score} 
                          className={
                            score === 'Strong' ? 'bg-green-100 text-green-800' :
                            score === 'Adequate' ? 'bg-blue-100 text-blue-800' :
                            score === 'Needs Improvement' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }
                        >
                          {score}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Severity Distribution */}
                  <div className="mb-6">
                    <p className="text-sm font-medium text-text-muted mb-2">Concern Severity Distribution</p>
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(iepSummary.iep_reviews?.severity_distribution || {}).map(([severity, count]) => (
                        <Badge 
                          key={severity} 
                          className={
                            severity === 'Critical' ? 'bg-red-100 text-red-800' :
                            severity === 'High' ? 'bg-orange-100 text-orange-800' :
                            severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }
                        >
                          {severity}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Top Concern Areas */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-text-muted mb-2">Top Concern Areas</p>
                      <div className="space-y-2">
                        {(iepSummary.iep_reviews?.top_concern_areas || []).slice(0, 8).map(([area, count], idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-amber-50 rounded">
                            <span className="text-sm text-text-primary">{area}</span>
                            <Badge variant="outline">{count}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-muted mb-2">Top Strength Areas</p>
                      <div className="space-y-2">
                        {(iepSummary.iep_reviews?.top_strength_areas || []).slice(0, 8).map(([area, count], idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded">
                            <span className="text-sm text-text-primary">{area}</span>
                            <Badge variant="outline" className="bg-green-100 text-green-800">{count}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Goal Trackers Summary */}
                <div className="bg-white rounded-xl border p-6">
                  <h2 className="font-heading font-bold text-lg text-text-primary mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    Goal Trackers Summary
                  </h2>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-text-primary">{iepSummary.goal_trackers?.total_trackers || 0}</p>
                      <p className="text-xs text-text-muted">Total Trackers</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-600">{iepSummary.goal_trackers?.status_distribution?.active || 0}</p>
                      <p className="text-xs text-text-muted">Active</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-600">{iepSummary.goal_trackers?.status_distribution?.achieved || 0}</p>
                      <p className="text-xs text-text-muted">Achieved</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-600">{iepSummary.goal_trackers?.overdue_count || 0}</p>
                      <p className="text-xs text-text-muted">Overdue IEPs</p>
                    </div>
                  </div>

                  {/* Upcoming IEPs */}
                  {iepSummary.goal_trackers?.upcoming_ieps?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-text-muted mb-2">Upcoming/Overdue IEPs</p>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {iepSummary.goal_trackers.upcoming_ieps.slice(0, 15).map((iep, idx) => (
                          <div 
                            key={idx} 
                            className={`flex items-center justify-between p-3 rounded ${
                              iep.is_overdue ? 'bg-red-50 border border-red-200' : 
                              iep.days_until <= 14 ? 'bg-amber-50 border border-amber-200' : 
                              'bg-gray-50'
                            }`}
                          >
                            <div>
                              <p className="font-medium text-text-primary text-sm">{iep.student_name}</p>
                              <p className="text-xs text-text-muted">{iep.goal_title}</p>
                            </div>
                            <div className="text-right">
                              <Badge className={
                                iep.is_overdue ? 'bg-red-100 text-red-800' :
                                iep.days_until <= 14 ? 'bg-amber-100 text-amber-800' :
                                'bg-gray-100 text-gray-800'
                              }>
                                {iep.is_overdue ? `${Math.abs(iep.days_until)}d overdue` : `${iep.days_until}d left`}
                              </Badge>
                              <p className="text-xs text-text-muted mt-1">{iep.due_date}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* IEP Goals Summary */}
                <div className="bg-white rounded-xl border p-6">
                  <h2 className="font-heading font-bold text-lg text-text-primary mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" />
                    IEP Goals Summary
                  </h2>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-text-primary">{iepSummary.iep_goals?.total_goals || 0}</p>
                      <p className="text-xs text-text-muted">Total Goals Created</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-text-primary">
                        {Object.keys(iepSummary.iep_goals?.by_domain || {}).length}
                      </p>
                      <p className="text-xs text-text-muted">Unique Domains</p>
                    </div>
                  </div>

                  {iepSummary.iep_goals?.by_domain && Object.keys(iepSummary.iep_goals.by_domain).length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-text-muted mb-2">Goals by Domain</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(iepSummary.iep_goals.by_domain)
                          .sort((a, b) => b[1] - a[1])
                          .map(([domain, count]) => (
                            <Badge key={domain} variant="outline">{domain}: {count}</Badge>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Recent Concerns Detail */}
                {iepSummary.iep_reviews?.recent_concerns?.length > 0 && (
                  <Collapsible>
                    <div className="bg-white rounded-xl border overflow-hidden">
                      <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-gray-50">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-500" />
                          <span className="font-semibold text-text-primary">Recent Concerns Detail</span>
                          <Badge variant="outline">{iepSummary.iep_reviews.recent_concerns.length}</Badge>
                        </div>
                        <ChevronDown className="w-5 h-5 text-text-muted" />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="border-t p-4 space-y-3 max-h-96 overflow-y-auto">
                          {iepSummary.iep_reviews.recent_concerns.slice(0, 30).map((concern, idx) => (
                            <div key={idx} className="p-3 bg-amber-50 rounded-lg">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <Badge className={
                                  concern.severity === 'Critical' ? 'bg-red-100 text-red-800' :
                                  concern.severity === 'High' ? 'bg-orange-100 text-orange-800' :
                                  concern.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-blue-100 text-blue-800'
                                }>
                                  {concern.severity}
                                </Badge>
                                <span className="text-xs text-text-muted">{concern.area}</span>
                              </div>
                              <p className="text-sm text-text-primary">{concern.issue}</p>
                              <p className="text-xs text-text-muted mt-1 italic">{concern.legal_basis}</p>
                              <div className="flex justify-between items-center mt-2 text-xs text-text-muted">
                                <span>{concern.document_name}</span>
                                <span>{concern.date ? new Date(concern.date).toLocaleDateString() : ''}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Button onClick={fetchIepSummary} className="rounded-full">Load IEP Summary</Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Admin;
